import { z } from 'zod';

const text = z.string().trim().min(1).max(20000);
export const questionCategory = z.enum(['fact', 'interpretation', 'schema', 'rendering']);
export const questionAction = z.enum([
    'answer',
    'request_document',
    'reject_out_of_scope',
    'defer',
    'reopen',
    'code_change',
    'confirm_code_change',
]);
export const questionEvidence = z.strictObject({
    document: z.string().min(1).max(1000),
    page: z.number().int().positive().nullable(),
    excerpt: text,
});
export const arbitrationCommands = z.discriminatedUnion('operation', [
    z.strictObject({
        operation: z.literal('open-question'),
        id: z.uuid(),
        runId: z.uuid(),
        dataScope: z.literal('public'),
        fields: z.array(z.string().min(1).max(300)).min(1).max(100),
        category: questionCategory,
        nature: z.enum(['contradiction', 'missing_document', 'uncertain', 'unsupported']),
        prompt: text,
        evidence: z.array(questionEvidence).max(100),
        alternatives: z.array(text).max(20),
        importance: z.enum(['low', 'normal', 'critical']),
        blocking: z.boolean(),
    }),
    z.strictObject({
        operation: z.literal('respond-question'),
        id: z.uuid(),
        questionId: z.uuid(),
        expectedEventId: z.uuid().nullable(),
        action: questionAction,
        codeRevision: z
            .string()
            .regex(/^[a-f0-9]{40}$/)
            .optional(),
        resolution: text,
        justification: text,
        evidence: z.array(questionEvidence).max(100),
    }),
    z.strictObject({
        operation: z.literal('resume-analysis'),
        id: z.uuid(),
        runId: z.uuid(),
        expectedStateHash: z.string().regex(/^[a-f0-9]{64}$/),
        confirmBlockersReviewed: z.literal(true),
        justification: text,
    }),
    z.strictObject({ operation: z.literal('list-learning-reviews'), page: z.number().int().positive().max(100000) }),
    z.strictObject({ operation: z.literal('get-arbitration'), runId: z.uuid() }),
    z.strictObject({ operation: z.literal('list-arbitrations'), page: z.number().int().positive().max(100000) }),
]);
export type ArbitrationCommand = z.infer<typeof arbitrationCommands>;
export const arbitrationRecord = z.object({
    id: z.uuid(),
    payload: z.json(),
    payloadHash: z.string(),
    actorId: z.uuid(),
    createdAt: z.string(),
});
// Durable read projections deliberately do not reuse today's command limits/enums.
// Preserve unknown payload fields in snapshots/copies so their original hashes stay verifiable.
const storedEvidence = z.looseObject({ document: z.string(), page: z.number().nullable(), excerpt: z.string() });
export const storedQuestion = z.looseObject({
    operation: z.literal('open-question'),
    id: z.uuid(),
    runId: z.uuid(),
    dataScope: z.literal('public'),
    fields: z.array(z.string()),
    category: z.string(),
    nature: z.string(),
    prompt: z.string(),
    evidence: z.array(storedEvidence),
    alternatives: z.array(z.string()),
    importance: z.string(),
    blocking: z.boolean(),
    carriedFrom: z
        .looseObject({
            questionId: z.uuid(),
            runId: z.uuid(),
            state: z.enum(['open', 'waiting_document', 'deferred', 'code_change']),
            decisionId: z.uuid().nullable(),
        })
        .optional(),
});
export const storedQuestionResponse = z.looseObject({
    id: z.uuid(),
    questionId: z.uuid(),
    action: z.string(),
    resolution: z.string(),
    justification: z.string(),
    evidence: z.array(storedEvidence),
    codeRevision: z.string().optional(),
});
export const questionView = z.strictObject({
    question: arbitrationRecord.extend({ payload: storedQuestion }),
    events: z.array(arbitrationRecord.extend({ payload: storedQuestionResponse })),
    state: z.enum(['open', 'resolved', 'waiting_document', 'deferred', 'code_change']),
    decisionId: z.uuid().nullable(),
});
export const arbitrationView = z.strictObject({
    run: arbitrationRecord,
    caseId: z.uuid(),
    title: z.string(),
    purpose: z.enum(['development', 'validation']),
    isCurrent: z.boolean(),
    stateHash: z.string(),
    questions: z.array(questionView),
    resumptions: z.array(arbitrationRecord),
    decisions: z.array(arbitrationRecord),
    execution: z.literal('engine_unavailable'),
});
export type ArbitrationView = z.infer<typeof arbitrationView>;
export type QuestionView = z.infer<typeof questionView>;
export const arbitrationQueue = z.strictObject({
    items: z.array(
        z.strictObject({
            caseId: z.uuid(),
            runId: z.uuid(),
            title: z.string(),
            open: z.number(),
            blocking: z.number(),
        }),
    ),
    hasMore: z.boolean(),
});

export const learningReviewQueue = z.strictObject({
    items: z.array(
        z.strictObject({
            revision: arbitrationRecord,
            decision: arbitrationRecord,
            runId: z.uuid(),
            state: z.enum(['proposed', 'active', 'superseded', 'retired']),
        }),
    ),
    hasMore: z.boolean(),
});
